import React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Chip, Collapse, Link, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { salesforceHost } from '~/services/common';

import DealOptionDetails, { type DealOptionDetailTab } from './DealOptionDetails';
import DealOptionsTableHeader from './DealOptionsTableHeader';
import {
  DEFAULT_CURRENCY,
  formatAllocation,
  formatDealOptionCurrency,
  formatDealOptionPercent,
} from './dealOptionsFormatters';
import type { DealOptionGroup, DealOptionRow } from './dealOptionsModel';
import { type SelectedRoomInfo, getMarginCheckColor, getRoomInfoCount } from './dealOptionsRoomInfo';

interface Props {
  activeDetailTabs: Record<string, DealOptionDetailTab>;
  expandedGroups: Set<string>;
  expandedRows: Set<string>;
  groups: Array<DealOptionGroup>;
  onActiveDetailTabChange: (rowId: string, tab: DealOptionDetailTab) => void;
  onOpenRoomInfo: (group: DealOptionGroup, roomInfo: Omit<SelectedRoomInfo, 'group'>) => void;
  onToggleGroup: (groupId: string) => void;
  onToggleRow: (rowId: string) => void;
  resolveRoomInfo: (group: DealOptionGroup) => Omit<SelectedRoomInfo, 'group'>;
}

function getSalesforceDealUrl(deal: OpportunityRelatedDeal) {
  if (!salesforceHost || !deal.sfid) {
    return undefined;
  }

  return `${salesforceHost}/${deal.sfid}`;
}

function getVisibleRowId(row: DealOptionRow, rowIndex: number) {
  return row.id || `${row.roomName}:${row.name}:${rowIndex}`;
}

export default function DealOptionsTable({
  activeDetailTabs,
  expandedGroups,
  expandedRows,
  groups,
  onActiveDetailTabChange,
  onOpenRoomInfo,
  onToggleGroup,
  onToggleRow,
  resolveRoomInfo,
}: Props) {
  return (
    <Table size="small" sx={{ minWidth: 1220 }}>
      <DealOptionsTableHeader />
      <TableBody>
        {groups.length === 0 && (
          <TableRow>
            <TableCell colSpan={8}>
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                No Salesforce deal options found for this offer.
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {groups.map((group) => {
          const isGroupExpanded = expandedGroups.has(group.id);
          const roomInfo = resolveRoomInfo(group);
          const roomInfoCount = getRoomInfoCount({
            blackouts: roomInfo.blackouts,
            egsRows: roomInfo.egsRows,
            surcharges: roomInfo.surcharges,
          });

          return (
            <React.Fragment key={group.id}>
              <TableRow
                hover
                sx={{ cursor: 'pointer', '& .MuiTableCell-root': { bgcolor: 'grey.50' } }}
                onClick={() => onToggleGroup(group.id)}
              >
                <TableCell>{isGroupExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
                <TableCell colSpan={7}>
                  <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
                    <Typography fontWeight={700} variant="body2">
                      {group.roomName}{' '}
                      <Typography color="text.secondary" component="span" variant="body2">
                        ({group.deals.length})
                      </Typography>
                    </Typography>
                    {roomInfoCount > 0 && (
                      <Button
                        size="small"
                        startIcon={<InfoIcon />}
                        sx={{ borderRadius: 10, textTransform: 'none', whiteSpace: 'nowrap' }}
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenRoomInfo(group, roomInfo);
                        }}
                      >
                        EGS, surcharges & blackouts
                        <Chip label={roomInfoCount} size="small" sx={{ height: 20, ml: 1 }} />
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>

              {isGroupExpanded &&
                group.deals.map((dealRow, rowIndex) => {
                  const rowId = getVisibleRowId(dealRow, rowIndex);
                  const isRowExpanded = expandedRows.has(rowId);
                  const salesforceDealUrl = getSalesforceDealUrl(dealRow.deal);

                  return (
                    <React.Fragment key={rowId}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onToggleRow(rowId)}>
                        <TableCell align="center">
                          {isRowExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {salesforceDealUrl ? (
                              <Link
                                href={salesforceDealUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                                underline="hover"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Stack alignItems="center" direction="row" spacing={0.5}>
                                  <span>{dealRow.name}</span>
                                  <OpenInNewIcon fontSize="inherit" />
                                </Stack>
                              </Link>
                            ) : (
                              dealRow.name
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">{formatAllocation(dealRow.allocation)}</TableCell>
                        <TableCell align="center">{formatAllocation(dealRow.maxExtraNights)}</TableCell>
                        <TableCell align="right">
                          <Stack spacing={0.25}>
                            <Typography fontWeight={600} variant="body2">
                              {formatDealOptionCurrency(dealRow.currency, dealRow.costPrice)}
                            </Typography>
                            {dealRow.convertedCost && dealRow.convertedCost !== dealRow.costPrice && (
                              <Typography color="text.secondary" variant="caption">
                                {formatDealOptionCurrency(DEFAULT_CURRENCY, dealRow.convertedCost)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {formatDealOptionCurrency(dealRow.currency, dealRow.sellPrice)}
                        </TableCell>
                        <TableCell align="right">
                          {dealRow.costNight === null
                            ? '—'
                            : formatDealOptionCurrency(dealRow.currency, dealRow.costNight)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography color={getMarginCheckColor(dealRow.marginCheck)} fontWeight={700} variant="body2">
                            {formatDealOptionPercent(dealRow.marginPercent)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ borderBottom: isRowExpanded ? 1 : 0, p: 0 }}>
                          <Collapse unmountOnExit in={isRowExpanded} timeout="auto">
                            <DealOptionDetails
                              activeTab={activeDetailTabs[rowId] ?? 'inclusions'}
                              row={dealRow}
                              onActiveTabChange={(tab) => onActiveDetailTabChange(rowId, tab)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
